/**
 * 後端資料 ↔ 設計稿語彙的對應層。
 *
 * 後端使用數字 enum（TaskStatusEnum 0..4）與 issue_type.key（epic/story/sub_task…），
 * 設計稿使用字串鍵（pending/progress/review/done/closed、epic/story/subtask/tech/explore…）。
 * 所有頁面一律先用這裡的函式轉換，再交給 design/ui.js 產生樣式。
 */

import { TYPE } from "./ui";

/* ---------- 工項狀態 ---------- */

/** TaskStatusEnum → 設計稿狀態鍵 */
export const STATUS_BY_CODE = ["pending", "progress", "review", "done", "closed"];
/** 設計稿狀態鍵 → TaskStatusEnum */
export const STATUS_CODE = { pending: 0, progress: 1, review: 2, done: 3, closed: 4 };
/** 設計稿狀態鍵 → 中文（下拉選單用） */
export const STATUS_LABEL = {
  pending: "待處理",
  progress: "進行中",
  review: "審核中",
  done: "已完成",
  closed: "已關閉",
};
/** 中文 → 設計稿狀態鍵 */
export const LABEL_TO_STATUS = {
  待處理: "pending",
  進行中: "progress",
  審核中: "review",
  已完成: "done",
  已關閉: "closed",
};

export function statusKey(v) {
  if (v == null) return "pending";
  if (typeof v === "number") return STATUS_BY_CODE[v] || "pending";
  const s = String(v);
  if (STATUS_CODE[s] !== undefined) return s;
  if (LABEL_TO_STATUS[s]) return LABEL_TO_STATUS[s];
  const n = Number(s);
  return Number.isNaN(n) ? "pending" : STATUS_BY_CODE[n] || "pending";
}

/* ---------- 優先級 ---------- */

export const PRIO_BY_CODE = ["low", "medium", "high", "urgent"];
export const PRIO_CODE = { low: 0, medium: 1, high: 2, urgent: 3 };
export const PRIO_LABEL = { urgent: "緊急", high: "高", medium: "一般", low: "低" };
export const LABEL_TO_PRIO = { 緊急: "urgent", 高: "high", 一般: "medium", 中: "medium", 低: "low" };

export function prioKey(v) {
  if (v == null) return "medium";
  if (typeof v === "number") return PRIO_BY_CODE[v] || "medium";
  const s = String(v);
  if (PRIO_CODE[s] !== undefined) return s;
  if (LABEL_TO_PRIO[s]) return LABEL_TO_PRIO[s];
  const n = Number(s);
  return Number.isNaN(n) ? "medium" : PRIO_BY_CODE[n] || "medium";
}

/* ---------- 專案狀態 ---------- */

export const PROJ_STATUS_BY_CODE = ["planning", "progress", "paused", "done", "cancelled"];
export const PROJ_STATUS_CODE = {
  planning: 0,
  progress: 1,
  paused: 2,
  done: 3,
  cancelled: 4,
};
export const PROJ_STATUS_LABEL = {
  planning: "規劃中",
  progress: "進行中",
  paused: "暫停",
  done: "已完成",
  cancelled: "已取消",
};
export const LABEL_TO_PROJ_STATUS = {
  規劃中: "planning",
  進行中: "progress",
  暫停: "paused",
  已完成: "done",
  已取消: "cancelled",
};

export function projStatusKey(v) {
  if (v == null) return "planning";
  if (typeof v === "number") return PROJ_STATUS_BY_CODE[v] || "planning";
  const s = String(v);
  if (PROJ_STATUS_CODE[s] !== undefined) return s;
  if (LABEL_TO_PROJ_STATUS[s]) return LABEL_TO_PROJ_STATUS[s];
  const n = Number(s);
  return Number.isNaN(n) ? "planning" : PROJ_STATUS_BY_CODE[n] || "planning";
}

/* ---------- 議題型別 ---------- */

/** 後端 issue_type.key → 設計稿型別鍵 */
export const ISSUE_TYPE_MAP = {
  initiative: "initiative",
  epic: "epic",
  story: "story",
  task: "task",
  bug: "bug",
  spike: "explore",
  tech_debt: "tech",
  sub_task: "subtask",
};

/** 設計稿型別鍵 → 後端 issue_type.key */
export const TYPE_TO_ISSUE_KEY = Object.entries(ISSUE_TYPE_MAP).reduce((a, [k, v]) => {
  a[v] = k;
  return a;
}, {});

/** 設計稿型別鍵 → 中文（下拉選單用，順序同設計稿 formConfig） */
export const TYPE_OPTIONS = [
  /*
   * 前五個與順序都照設計稿 el-pm-template_4.html L1152-L1158 的「這是哪一種」。
   * 「分不出來就選工作」，所以工作排第一。
   *
   * 設計稿的下拉只列這五個，但這裡保留另外三個 —— 資料庫裡已經有這些型別的
   * 工項，選單拿掉只會變成「看得到、改不回去」。要不要停用它們是產品決定，
   * 不是改版順手做掉的事。
   */
  "工作",
  "問題修正",
  "大項目",
  "中項目",
  "小項目",
  /* 策略主題原本漏在選單外：LABEL_TO_TYPE 認得它、路線圖也在用，
     但下拉選不到，等於這個型別只能靠改資料庫產生 */
  "策略主題",
  "技術債",
  "探索",
];

/*
 * 中文 → 型別鍵。新舊兩套名字都要認得：
 * 顯示用的是白話名（大項目…），但後端與既有資料裡存的可能還是舊名（史詩…），
 * 只留一套的話其中一邊會全部落到預設的「工作」。
 */
export const LABEL_TO_TYPE = {
  /* 設計稿的白話名 */
  工作: "task",
  問題修正: "bug",
  大項目: "epic",
  中項目: "story",
  小項目: "subtask",
  /* 舊名，資料庫與後端可能還在用 */
  任務: "task",
  缺陷: "bug",
  史詩: "epic",
  使用者故事: "story",
  子任務: "subtask",
  策略主題: "initiative",
  技術債: "tech",
  探索: "explore",
};

export function typeKey(v) {
  if (!v) return "task";
  const s = String(v);
  if (TYPE[s]) return s;
  if (ISSUE_TYPE_MAP[s]) return ISSUE_TYPE_MAP[s];
  if (LABEL_TO_TYPE[s]) return LABEL_TO_TYPE[s];
  return "task";
}

/* ---------- 專案成員角色 ---------- */

export const MEMBER_ROLE_BY_CODE = ["專案經理", "開發", "測試", "設計", "觀察者"];
export const MEMBER_ROLE_CODE = {
  專案經理: 0,
  開發: 1,
  測試: 2,
  設計: 3,
  觀察者: 4,
};
export function memberRoleLabel(v) {
  if (typeof v === "number") return MEMBER_ROLE_BY_CODE[v] || "觀察者";
  return v || "觀察者";
}

/* ---------- 審核 ---------- */

export const REVIEW_TYPE_BY_CODE = ["程式碼審查", "測試驗證", "PO 驗收"];
export const REVIEW_TYPE_CODE = { "程式碼審查": 0, "測試驗證": 1, "PO 驗收": 2 };
export const REVIEW_STATUS_BY_CODE = ["pending", "approved", "rejected", "returned"];
export const REVIEW_STATUS_LABEL = {
  pending: "待審",
  approved: "通過",
  rejected: "要求修改",
  returned: "已退回",
};

export function reviewTypeLabel(v) {
  if (typeof v === "number") return REVIEW_TYPE_BY_CODE[v] || "程式碼審查";
  return v || "程式碼審查";
}
export function reviewStatusKey(v) {
  if (typeof v === "number") return REVIEW_STATUS_BY_CODE[v] || "pending";
  return v || "pending";
}

/**
 * 把關者按下「通過／要求修改」之後，用一句話講「真的發生了什麼」。
 *
 * 審核通過不用再有人去按一次「完成」：後端 ReviewService.Decide 在整張審核單通過時
 * 自己把工項推到「已完成」，任一把關者要求修改時退回「進行中」。
 * 但這句話不能只憑「我按的是哪一顆」就寫 —— 有兩種情況工項不會跟著變：
 *   · 同一張審核單掛了好幾個把關者，我通過了、別人還沒看，審核單還是待審；
 *   · 自動轉換被其他守門擋下（例如還有驗收條件沒勾），審核單結了、工項還停在原地。
 * 所以呼叫端送出後要重讀一次審核單與工項，把讀到的狀態交進來；讀不到就傳 null，
 * 這裡退回只講「我這一票」的說法，不替系統保證它沒保證的事。
 *
 * 審核頁（pages/Approval/View.js）與工項頁（components/task/TaskDetailPanel.js）
 * 都用這一支，同一個動作在兩個地方不能講成兩種結果。
 *
 * @param {number} decision 1 = 通過、2 = 要求修改（後端 ReqReviewDecision）
 * @param {string|null} reviewKey 送出後重讀的審核單狀態（reviewStatusKey 的值）
 * @param {string|null} taskKey 送出後重讀的工項狀態（statusKey 的值）
 * @param {number|null} [progress] 送出後重讀的工項進度。退回時後端只把「超過 90%」的拉回 90%，
 *   原本就不到 90% 的不動 —— 所以進度要照讀回來的數字講，不能一律寫「拉回 90%」
 * @returns {{ text: string, tone: (string|undefined), ms: (number|undefined) }}
 *   tone / ms 直接交給 toast：出乎預期的結果用 error 色、停久一點，因為那句話是他唯一的線索
 */
export function reviewOutcome(decision, reviewKey, taskKey, progress = null) {
  const now = taskKey ? STATUS_LABEL[taskKey] || "" : "";
  const pct = progress == null || Number.isNaN(Number(progress)) ? null : Number(progress);
  if (Number(decision) === 1) {
    if (reviewKey === "approved" && taskKey === "done") return { text: "已通過，工項已完成" };
    if (reviewKey === "pending") {
      return { text: "您已通過，尚待其他審核人審核；全數通過後才會變更為「已完成」", ms: 5000 };
    }
    // 讀回來是退回：同一張單上別人先退了，這一票改變不了結果，不能講成「已通過」
    if (reviewKey === "rejected") {
      return {
        text: "此審核已被退回要求修改，您的「通過」不影響結果；工項將退回修改，修改完成後會重新送審",
        tone: "error",
        ms: 6000,
      };
    }
    if (reviewKey === "approved" && taskKey === "review") {
      return {
        text: "已通過，但工項未自動變更為「已完成」，仍停留於「審核中」；原因請至工項頁的「⋯ 更多」查看",
        tone: "error",
        ms: 6000,
      };
    }
    if (reviewKey === "approved" && now) {
      return {
        text: `已通過，但工項目前為「${now}」、不在審核中，因此未自動變更為「已完成」；本次通過仍然有效，請至工項頁將其變更為「已完成」`,
        tone: "error",
        ms: 6000,
      };
    }
    return { text: "已通過" };
  }
  if (reviewKey === "rejected" && taskKey === "progress") {
    return {
      text:
        pct == null
          ? "已退回，狀態變更為進行中；負責人修改完成並將進度調整為 100% 後會重新送審"
          : `已退回，狀態變更為進行中（進度 ${pct}%）；負責人修改完成並將進度調整為 100% 後會重新送審`,
    };
  }
  if (reviewKey === "rejected" && now) {
    return {
      text: `已退回要求修改，但工項仍停留於「${now}」，未自動變更為「進行中」`,
      tone: "error",
      ms: 6000,
    };
  }
  return { text: "已退回，已要求負責人修改" };
}

/* ---------- 里程碑狀態 ---------- */

/** 里程碑：0 未開始 / 1 進行中 / 2 已完成 → 借用工項狀態色 */
export function phaseStatusKey(v) {
  const n = typeof v === "number" ? v : Number(v);
  if (n === 2) return "done";
  if (n === 1) return "progress";
  return "pending";
}

/* ---------- 日期 ---------- */

/** ISO / DateTimeOffset → 設計稿的 `2026/7/3` */
export function fmtDate(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
}

/** ISO → `2026/7/24 09:12` */
export function fmtDateTime(v) {
  if (!v) return "—";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** ISO → `2026-07-24`（<input type="date"> 用） */
export function fmtInputDate(v) {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** 相對時間：5 分鐘前 / 2 小時前 / 昨天 / 2 天前 / 日期 */
export function fmtRelative(v) {
  if (!v) return "";
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "剛剛";
  if (diff < 3600) return `${Math.floor(diff / 60)} 分鐘前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} 小時前`;
  if (diff < 172800) return "昨天";
  if (diff < 604800) return `${Math.floor(diff / 86400)} 天前`;
  return fmtDate(d);
}

/** 是否逾期（未完成 / 未關閉且截止日已過） */
export function isOverdue(status, due) {
  const k = statusKey(status);
  if (k === "done" || k === "closed") return false;
  if (!due) return false;
  const d = new Date(String(due).replace(/\//g, "-"));
  if (Number.isNaN(d.getTime())) return false;
  return d < new Date();
}

/** 兩日期相差天數 */
export function daysBetween(a, b) {
  const x = new Date(a);
  const y = new Date(b);
  if (Number.isNaN(x.getTime()) || Number.isNaN(y.getTime())) return 0;
  return Math.round((y - x) / 86400000);
}

/* ---------- 工項正規化 ---------- */

/**
 * 把後端任務物件轉成設計稿頁面統一使用的形狀。
 * 後端欄位命名不一（有 snake_case 也有 PascalCase），這裡一次吸收。
 */
export function normalizeTask(t = {}) {
  const g = (...keys) => {
    for (const k of keys) {
      if (t[k] !== undefined && t[k] !== null) return t[k];
    }
    return undefined;
  };
  const status = statusKey(g("status", "Status"));
  const rollupTotal = Number(g("rollup_total_count", "rollupTotalCount") || 0);
  const rollupDone = Number(g("rollup_done_count", "rollupDoneCount") || 0);
  const est = g("estimated_hours", "estimatedHours", "rollup_estimated_hours");
  const progress = Number(g("progress", "Progress", "rollup_progress") || 0);

  return {
    id: g("id", "Id"),
    code: g("issue_key", "issueKey") || `#${g("id", "Id") ?? ""}`,
    name: g("title", "Title", "name") || "",
    desc: g("description", "Description") || "",
    type: typeKey(g("issue_type_key", "issueTypeKey", "issue_type", "type")),
    status,
    prio: prioKey(g("priority", "Priority")),
    assignee: g("assignee_name", "assigneeName", "assignee", "Assignee") || "",
    assigneeAccount: g("assignee", "Assignee") || "",
    projectId: g("project_id", "projectId"),
    projectName: g("project_name", "projectName") || "",
    phaseId: g("phase_id", "phaseId"),
    parentId: g("parent_task_id", "parentTaskId"),
    depth: Number(g("depth", "Depth") || 0),
    start: fmtDate(g("start_date", "startDate")),
    startRaw: g("start_date", "startDate"),
    due: fmtDate(g("due_date", "dueDate")),
    dueRaw: g("due_date", "dueDate"),
    created: fmtDateTime(g("create_at", "createAt")),
    completedAt: g("completed_at", "completedAt"),
    score: g("score", "Score") ?? null,
    est: est == null ? null : Number(est),
    act: Number(g("actual_hours", "actualHours") || 0),
    progress,
    labels: (() => {
      const raw = g("tags", "Tags", "labels");
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      return String(raw)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    })(),
    prog:
      rollupTotal > 0
        ? { done: rollupDone, total: rollupTotal, pct: progress, est: est == null ? 0 : Number(est) }
        : null,
    overdue: isOverdue(status, g("due_date", "dueDate")),
    raw: t,
  };
}

/** 把後端專案物件轉成設計稿形狀 */
export function normalizeProject(p = {}) {
  const g = (...keys) => {
    for (const k of keys) {
      if (p[k] !== undefined && p[k] !== null) return p[k];
    }
    return undefined;
  };
  /* 計畫起訖（最早開始 ~ 最晚截止）。儀表板 RAG 靠這組算落後幅度，不要拿實際期間去替換 */
  const start = g("start_date", "startDate");
  const end = g("end_date", "endDate");
  /*
   * 實際執行期間：後端由工項推導（全部結案的收在最後完成時間，還在跑的到今天）。
   * 後端還沒更新時退回計畫起訖，畫面才不會整欄變成「—」。
   */
  const aStart = g("actual_start_date", "actualStartDate") || start;
  let aEnd = g("actual_end_date", "actualEndDate") || end;
  /* 工項全排在未來的專案，實際迄日（今天）會早於起日，直接顯示會變成反向區間 */
  if (aStart && aEnd && new Date(aEnd) < new Date(aStart)) aEnd = aStart;
  const nowYear = new Date().getFullYear();
  const f = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    // 設計稿 projectsData：同年只寫 `6/1`，跨年才補年份（`9/1 – 2027/2/1`）
    const md = `${d.getMonth() + 1}/${d.getDate()}`;
    return d.getFullYear() === nowYear ? md : `${d.getFullYear()}/${md}`;
  };
  return {
    id: g("id", "Id"),
    code: g("code", "Code", "project_code") || "",
    name: g("name", "Name") || "",
    client: g("client_name", "clientName", "client") || "內部",
    desc: g("description", "Description") || "",
    members: Number(g("member_count", "memberCount", "members") || 0),
    tasks: Number(g("task_count", "taskCount", "tasks") || 0),
    pct: Number(g("progress", "Progress", "pct") || 0),
    status: projStatusKey(g("status", "Status")),
    prio: prioKey(g("priority", "Priority")),
    startRaw: start,
    endRaw: end,
    actualStartRaw: aStart,
    actualEndRaw: aEnd,
    /* 「期間」欄顯示的是實際執行期間，不是計畫期間 —— 專案沒有人去定義起訖日，
       要看的是這個案子實際從什麼時候做到什麼時候 */
    range: aStart || aEnd ? `${f(aStart)} – ${f(aEnd)}` : "—",
    estimatedHours: g("estimated_hours", "estimatedHours"),
    /* 舊欄位也讀：正式庫跑 V020 之前回的還是舊的兩個鍵，這樣新舊都吃得到 */
    gitUrl: g("git_url", "gitUrl", "git_frontend_url", "gitFrontendUrl", "git_backend_url", "gitBackendUrl"),
    raw: p,
  };
}

/**
 * 富文本 HTML → 一行純文字。
 *
 * 給只能放一行的地方用（活動時間軸、清單摘要…）。段落界線換成空白，
 * 否則兩段會黏成一個詞。有圖片時補一個「[圖片]」標記 —— 直接吃掉的話，
 * 一則只有截圖的回報在時間軸上會變成空白，看起來像壞掉。
 *
 * opts.noImageMark：呼叫端**自己會把圖畫出來**時才傳 true（工項頁「以前的紀錄」
 * 就是這樣：摘要維持一行，圖另外以縮圖列在底下）。標記是「看不到圖」的補償，
 * 圖都看得到了還留一句「[圖片×1]」只會變成雜訊。
 *
 * 用 DOMParser 而非 innerHTML：解析出來的是 inert document，
 * 不會載入圖片也不會觸發 onerror。
 */
export function toPlainText(html, opts) {
  const s = String(html || "");
  if (!s) return "";
  if (!/[<&]/.test(s)) return s.trim();
  const doc = new DOMParser().parseFromString(
    s.replace(/<(?:br|\/p|\/div|\/li|\/h[1-6]|\/blockquote)\s*\/?>/gi, "$& "),
    "text/html"
  );
  const text = (doc.body.textContent || "").replace(/\s+/g, " ").trim();
  const imgs = doc.body.querySelectorAll("img").length;
  if (!imgs || (opts && opts.noImageMark)) return text;
  return text ? `${text} [圖片×${imgs}]` : `[圖片×${imgs}]`;
}

/**
 * 圖片網址的白名單：站內絕對路徑（/InlineImage/…）、http(s)、以及舊資料還留著的 base64。
 * 過不了就回空字串。
 *
 * 放在 domain 而不是 RichView 裡面：RichView 用它決定「這張圖畫不畫得出來」，
 * imagesOf 用它決定「縮圖列要數幾張」。兩邊一定要是同一把尺 —— 不然畫面會寫
 * 「回報裡貼了 1 張圖，點圖看原尺寸」，而那個位置其實是一段點不動的
 * 「圖片不能顯示」，等於指著一個不存在的東西叫人去點。
 */
export function safeImgSrc(raw) {
  const v = String(raw || "").trim();
  if (!v) return "";
  // 站內絕對路徑。(?!\/) 是為了不把「//外站/x.png」當成站內路徑，不是為了擋掉它
  if (/^\/(?!\/)/.test(v)) return v;
  /*
   * 外部圖片。protocol-relative（//host/x.png）也放行：https://host/x.png 本來就放行，
   * 擋掉同一張圖的另一種寫法沒有多擋到任何風險，只會讓舊資料的截圖平白消失。
   * 外送請求的顧慮由 RichView 的 referrerpolicy=no-referrer 處理。
   */
  if (/^(https?:)?\/\/[^/]/i.test(v)) return v;
  // 改成「上傳成檔案」之前存下的回報仍是 base64，擋掉等於讓舊紀錄的圖整片消失
  if (/^data:image\/(png|jpe?g|gif|webp|bmp|avif);/i.test(v)) return v;
  return "";
}

/**
 * 富文本 HTML → 「只剩圖片」的 HTML（給縮圖列用）+ 畫得出來的張數 count
 * + 網址過不了白名單、只會變成「圖片不能顯示」那段文字的張數 blocked。
 *
 * 為什麼要這一支：活動時間軸一列只放得下一件事，整篇 HTML 塞進去會把時間軸撐爛；
 * 但回報的交付物常常**就是那張截圖**，剝成純文字等於把交付物藏起來。折衷是
 * 「摘要一行 + 底下一排縮圖」，這支負責把圖挑出來。
 *
 * 只搬 src/alt，其他屬性（含 on*）一律不帶；而且是用 DOM 建節點再讀 innerHTML，
 * 不是字串黏起來 —— 網址裡有引號也不會拼出破格的標籤。
 * 呼叫端拿到的字串仍會再經過 RichView 的白名單消毒，這裡是第一道而不是唯一一道。
 *
 * 一樣用 DOMParser（inert document）：挑圖的過程不會真的去載圖。
 */
export function imagesOf(html) {
  const s = String(html || "");
  const none = { html: "", count: 0, blocked: 0 };
  if (!s || !/<img/i.test(s)) return none;
  let doc = null;
  try {
    doc = new DOMParser().parseFromString(s, "text/html");
  } catch (e) {
    doc = null;
  }
  if (!doc || !doc.body) return none;
  const box = doc.createElement("div");
  let count = 0;
  let blocked = 0;
  Array.from(doc.body.querySelectorAll("img")).forEach((img) => {
    const src = img.getAttribute("src") || "";
    if (!src) return;
    const el = doc.createElement("img");
    el.setAttribute("src", src);
    // alt 一定要有值：縮圖載不到時，畫面上只剩 alt 講得出這裡本來有什麼
    el.setAttribute("alt", img.getAttribute("alt") || "回報附加的截圖");
    box.appendChild(el);
    /*
     * count 只數「RichView 畫得出來的」：網址過不了白名單的那幾張，RichView 會換成
     * 一段「圖片不能顯示」的靜態文字，數進去就會變成叫人去點一個點不動的東西。
     * 但它們仍然放進 html —— 那段說明文字要出現在縮圖列裡，使用者才知道
     * 「這裡本來有一張圖、它為什麼不見了」，而不是以為自己記錯。
     */
    if (safeImgSrc(src)) count += 1;
    else blocked += 1;
  });
  return count || blocked ? { html: box.innerHTML, count, blocked } : none;
}

/**
 * 留言的 @提及 染色（設計稿 mention()）。
 * 留言是使用者輸入，直接 innerHTML 會有 XSS，所以先跳脫再插入 span。
 *
 * 工項詳情與個人工項的處理回報都要用同一套呈現，所以放在這裡而不是各自寫一份。
 */
export function mentionHtml(text) {
  const esc = String(text || "").replace(
    /[&<>"]/g,
    (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[ch])
  );
  /*
   * 字元類要跟後端一字不差。
   *
   * 兩個後端都用 @([A-Za-z0-9_.\-]+) 抓「帳號」去建通知
   * （Web/Services/NotificationService.cs 的 HandleCommentMentions、
   *   Chat/Hubs/ChatHub.cs 的 NotifyMentions）。
   * 這裡原本是 @([一-龥A-Za-z]+)，兩個方向都錯：
   *   · @ming.huang 只標到 @ming —— 真正被通知的帳號跟畫面標的不是同一個
   *   · @王小明 標得很漂亮，但後端的字元類不含中文，那一則永遠不會通知任何人
   * 標起來卻不會通知，比不標還糟：寄件人以為講到了，對方根本沒收到。
   *
   * 連在後面的句號會被一起吃進去（"謝謝 @ming.huang." → ming.huang.），
   * 這一點後端也一樣 —— 刻意保持一致，兩邊對同一句話的解讀才不會分岔。
   */
  return esc.replace(
    /@([A-Za-z0-9_.-]+)/g,
    '<span style="color:var(--accent);font-weight:700;">@$1</span>'
  );
}
